import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Checkbox } from "../../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Database, Loader2, Play, Search, AlertCircle } from "lucide-react";
import { useToast } from "../../../contexts/ToastContext";
import { api } from "../../../services/api";
import { ConfirmationDialog } from "../../ui/confirmation-dialog";

export function AzureTierManager() {
  const { showToast } = useToast();
  const [prefix, setPrefix] = useState('done-summary/');
  const [targetTier, setTargetTier] = useState('Cool');
  const [scanning, setScanning] = useState(false);
  const [triggering, setTriggering] = useState(false);
  
  const [blobs, setBlobs] = useState<any[]>([]);
  const [selectedBlobs, setSelectedBlobs] = useState<string[]>([]);
  const [filterTier, setFilterTier] = useState('All');

  const filteredBlobs = blobs.filter(b => filterTier === 'All' || b.tier === filterTier);
  
  const [confirmationDialog, setConfirmationDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
  }>({
    open: false, title: '', description: '', onConfirm: () => { }
  });

  const handleScan = async () => {
    if (!prefix) {
      showToast({ type: 'error', title: 'Error', message: 'Prefix is required' });
      return;
    }

    setScanning(true);
    setBlobs([]);
    setSelectedBlobs([]);
    
    try {
      const result = await api.getArchivedBlobs(prefix, 'all');
      if (result.success) {
        setBlobs(result.data || []);
        if (result.data && result.data.length > 0) {
          showToast({ type: 'success', title: 'Scan Complete', message: `Found ${result.data.length} archived blobs.` });
        } else {
          showToast({ type: 'info', title: 'Scan Complete', message: 'No archived blobs found with this prefix.' });
        }
      } else {
        showToast({ type: 'error', title: 'Scan Failed', message: result.error || 'Failed to list blobs.' });
      }
    } catch (error: any) {
      showToast({ type: 'error', title: 'Error', message: error.message || 'Error scanning blobs.' });
    } finally {
      setScanning(false);
    }
  };

  const handleTrigger = () => {
    if (selectedBlobs.length === 0) {
      showToast({ type: 'error', title: 'Error', message: 'Select at least one blob to modify.' });
      return;
    }

    setConfirmationDialog({
      open: true,
      title: 'Change Access Tier',
      description: `Are you sure you want to change the access tier to ${targetTier} for ${selectedBlobs.length} selected blob(s)? This process may take hours to complete on Azure's side.`,
      onConfirm: async () => {
        setTriggering(true);
        setConfirmationDialog(prev => ({ ...prev, open: false }));
        try {
          const result = await api.triggerBlobTierChange({
            paths: selectedBlobs,
            targetTier: targetTier
          });

          if (result.success) {
            showToast({ type: 'success', title: 'Success', message: 'Tier change triggered. Monitor progress in the Scheduler tab.' });
            // Remove selected from list
            setBlobs(prev => prev.filter(b => !selectedBlobs.includes(b.name)));
            setSelectedBlobs([]);
          } else {
            showToast({ type: 'error', title: 'Error', message: result.error || 'Failed to trigger change.' });
          }
        } catch (error: any) {
          showToast({ type: 'error', title: 'Error', message: error.message });
        } finally {
          setTriggering(false);
        }
      }
    });
  };

  const toggleBlobSelection = (name: string) => {
    setSelectedBlobs(prev => 
      prev.includes(name) ? prev.filter(b => b !== name) : [...prev, name]
    );
  };

  const selectAllBlobs = () => {
    if (selectedBlobs.length === filteredBlobs.length) {
      setSelectedBlobs([]); // deselect all
    } else {
      setSelectedBlobs(filteredBlobs.map(b => b.name)); // select all
    }
  };

  const handleInlineTrigger = (blobName: string, target: string) => {
    setConfirmationDialog({
      open: true,
      title: 'Change Access Tier',
      description: `Are you sure you want to change the access tier of ${blobName} to ${target}?`,
      onConfirm: async () => {
        setTriggering(true);
        setConfirmationDialog(prev => ({ ...prev, open: false }));
        try {
          const result = await api.triggerBlobTierChange({
            paths: [blobName],
            targetTier: target
          });

          if (result.success) {
            showToast({ type: 'success', title: 'Success', message: `Tier change triggered for ${blobName}.` });
            // Update local blob state visually to pending or new tier
            setBlobs(prev => prev.map(b => b.name === blobName ? { ...b, tier: `${target} (Pending)` } : b));
          } else {
            showToast({ type: 'error', title: 'Error', message: result.error || 'Failed to trigger change.' });
          }
        } catch (error: any) {
          showToast({ type: 'error', title: 'Error', message: error.message });
        } finally {
          setTriggering(false);
        }
      }
    });
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5" />
            <span className="text-lg sm:text-xl">Azure Tier Manager</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            
            {/* Top Bar: Prefix and Scan */}
            <div className="flex flex-col sm:flex-row gap-4 items-end">
              <div className="space-y-2 flex-grow">
                <Label htmlFor="blob-prefix" className="text-xs">Scan Prefix (Path)</Label>
                <div className="flex gap-2">
                  <Input
                    id="blob-prefix"
                    value={prefix}
                    onChange={(e) => setPrefix(e.target.value)}
                    placeholder="e.g., done-summary/"
                    className="flex-grow"
                  />
                  <Button onClick={handleScan} disabled={scanning} className="h-10">
                    {scanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                    Scan Blob
                  </Button>
                </div>
              </div>
            </div>

            {/* Results Table */}
            {blobs.length > 0 && (
              <div className="space-y-2">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-muted/30 p-2 rounded-md">
                  <h4 className="text-sm font-semibold">
                    Blobs found ({filteredBlobs.length} {filterTier !== 'All' ? filterTier : ''})
                  </h4>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-muted-foreground">Filter:</Label>
                      <Select value={filterTier} onValueChange={setFilterTier}>
                        <SelectTrigger className="w-[110px] h-7 text-xs bg-background">
                          <SelectValue placeholder="All Tiers" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="All">All Tiers</SelectItem>
                          <SelectItem value="Archive">Archive</SelectItem>
                          <SelectItem value="Cool">Cool</SelectItem>
                          <SelectItem value="Hot">Hot</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button variant="outline" size="sm" onClick={selectAllBlobs} className="h-7 text-xs px-3">
                      {selectedBlobs.length === filteredBlobs.length ? "Deselect All" : "Select All"}
                    </Button>
                  </div>
                </div>
                
                <div className="border rounded-md max-h-[300px] overflow-y-auto">
                  <table className="w-full text-sm text-left align-middle border-collapse divide-y">
                    <thead className="bg-muted sticky top-0 z-10 text-muted-foreground text-xs font-semibold uppercase">
                      <tr>
                        <th className="px-3 py-2 w-10 text-center">
                          <Checkbox checked={selectedBlobs.length > 0 && selectedBlobs.length === filteredBlobs.length} onCheckedChange={selectAllBlobs} />
                        </th>
                        <th className="px-3 py-2">Blob Path</th>
                        <th className="px-3 py-2 w-20">Tier</th>
                        <th className="px-3 py-2 w-24">Size</th>
                        <th className="px-3 py-2 w-[120px] text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y text-xs">
                      {filteredBlobs.length === 0 ? (
                         <tr>
                           <td colSpan={5} className="text-center py-4 text-muted-foreground italic">No blobs match the selected filter.</td>
                         </tr>
                      ) : filteredBlobs.map((blob) => {
                        const isPending = Boolean(blob.archiveStatus) || (blob.tier && blob.tier.includes('Pending'));
                        let displayTier = blob.tier || 'Unknown';
                        if (blob.archiveStatus) {
                           const target = blob.archiveStatus.split('-to-')[1] || '';
                           displayTier = `Archive (Pending ${target.charAt(0).toUpperCase() + target.slice(1)})`;
                        }

                        return (
                        <tr key={blob.name} className={`hover:bg-muted/50 ${isPending ? 'opacity-70' : 'cursor-pointer'}`} onClick={() => !isPending && toggleBlobSelection(blob.name)}>
                          <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                            <Checkbox 
                              checked={selectedBlobs.includes(blob.name)}
                              onCheckedChange={() => !isPending && toggleBlobSelection(blob.name)}
                              disabled={isPending}
                            />
                          </td>
                          <td className="px-3 py-2 font-mono text-[11px] truncate max-w-[200px]" title={blob.name}>{blob.name}</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                             <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                               isPending ? 'bg-orange-100 text-orange-700 animate-pulse' :
                               blob.tier === 'Archive' ? 'bg-red-100 text-red-700' :
                               blob.tier === 'Cool' ? 'bg-blue-100 text-blue-700' :
                               blob.tier === 'Hot' ? 'bg-green-100 text-green-700' :
                               'bg-yellow-100 text-yellow-700'
                             }`}>
                               {displayTier}
                             </span>
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">{(blob.size / 1024 / 1024).toFixed(2)} MB</td>
                          <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                             <Select disabled={isPending} onValueChange={(val) => handleInlineTrigger(blob.name, val)}>
                                <SelectTrigger className="w-[100px] h-6 text-[10px] inline-flex">
                                  <SelectValue placeholder="Edit Tier" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Hot" className="text-[10px]">Set Hot</SelectItem>
                                  <SelectItem value="Cool" className="text-[10px]">Set Cool</SelectItem>
                                  <SelectItem value="Archive" className="text-[10px]">Set Archive</SelectItem>
                                </SelectContent>
                             </Select>
                          </td>
                        </tr>
                      );})}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Empty State / Status Note */}
             <div className="bg-yellow-50 text-yellow-800 p-3 rounded-md flex items-start gap-3 border border-yellow-200">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div className="text-xs">
                  <p className="font-semibold mb-1">Archived Blob Rehydration</p>
                  <p>When you change a blob's tier from Archive to Hot or Cool, Azure begins a rehydration process. This can take several hours manually. The file will not be accessible immediately.</p>
                </div>
              </div>

            {/* Action Bar */}
            {selectedBlobs.length > 0 && filterTier !== 'All' && (
              <div className="pt-4 border-t flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3 w-full sm:w-auto">
                  <Label className="text-xs whitespace-nowrap">Target Tier:</Label>
                  <Select value={targetTier} onValueChange={setTargetTier}>
                    <SelectTrigger className="w-[120px] h-8 text-xs">
                      <SelectValue placeholder="Target Tier" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Cool">Cool</SelectItem>
                      <SelectItem value="Hot">Hot</SelectItem>
                      <SelectItem value="Archive">Archive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <span className="text-xs text-muted-foreground whitespace-nowrap mr-2">
                    {selectedBlobs.length} selected
                  </span>
                  <Button 
                    onClick={handleTrigger} 
                    disabled={triggering || selectedBlobs.length === 0}
                    className="w-full sm:w-auto"
                  >
                    {triggering ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                    {triggering ? 'Triggering...' : 'Change Access Tier'}
                  </Button>
                </div>
              </div>
            )}

            {selectedBlobs.length > 0 && filterTier === 'All' && (
              <div className="pt-4 border-t text-sm text-yellow-600 dark:text-yellow-500 italic text-center font-medium bg-yellow-50/50 p-2 rounded">
                 ⚠️ Please select a specific tier from the "Filter" dropdown above (e.g., 'Archive' or 'Cool') to perform a bulk action.
              </div>
            )}
            
          </div>
        </CardContent>
      </Card>

      <ConfirmationDialog
        open={confirmationDialog.open}
        onOpenChange={(open) => setConfirmationDialog({ ...confirmationDialog, open })}
        title={confirmationDialog.title}
        description={confirmationDialog.description}
        onConfirm={confirmationDialog.onConfirm}
        confirmText="Yes, Change Tier"
        cancelText="Cancel"
      />
    </>
  );
}
